/**
 * Posizione del sole (azimut/elevazione) con l'algoritmo astronomico
 * semplificato NOAA/Meeus — precisione ben oltre il necessario per il calcolo
 * delle ombre urbane. Nessuna dipendenza esterna.
 */

export interface SunPosition {
  /** Azimut in gradi da Nord, in senso orario (0 = N, 90 = E, 180 = S, 270 = O). */
  azimuthDeg: number;
  /** Elevazione sull'orizzonte in gradi (negativa = sole sotto l'orizzonte). */
  elevationDeg: number;
}

const RAD = Math.PI / 180;
const DAY_MS = 86400000;
const J1970 = 2440588;
const J2000 = 2451545;
const OBLIQUITY = 23.4397 * RAD;

function toDays(dateMs: number): number {
  return dateMs / DAY_MS - 0.5 + J1970 - J2000;
}

function solarMeanAnomaly(d: number): number {
  return RAD * (357.5291 + 0.98560028 * d);
}

function eclipticLongitude(M: number): number {
  const C = RAD * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
  const P = RAD * 102.9372; // perielio della Terra
  return M + C + P + Math.PI;
}

function declination(L: number): number {
  return Math.asin(Math.sin(OBLIQUITY) * Math.sin(L));
}

function rightAscension(L: number): number {
  return Math.atan2(Math.sin(L) * Math.cos(OBLIQUITY), Math.cos(L));
}

function siderealTime(d: number, lwRad: number): number {
  return RAD * (280.16 + 360.9856235 * d) - lwRad;
}

/** Rifrazione atmosferica media (input/output in radianti, formula di Bennett). */
function refraction(hRad: number): number {
  const h = Math.max(hRad, -0.05);
  return 0.0002967 / Math.tan(h + 0.00312536 / (h + 0.08901179));
}

export function sunPosition(dateMs: number, lat: number, lng: number): SunPosition {
  const lw = -lng * RAD;
  const phi = lat * RAD;
  const d = toDays(dateMs);

  const M = solarMeanAnomaly(d);
  const L = eclipticLongitude(M);
  const dec = declination(L);
  const ra = rightAscension(L);
  const H = siderealTime(d, lw) - ra;

  const sinAlt = Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H);
  let altitude = Math.asin(Math.min(1, Math.max(-1, sinAlt)));
  altitude += refraction(altitude);

  // atan2(sin H, cos H · sin φ − tan dec · cos φ) misura da Sud verso Ovest
  const azFromSouth = Math.atan2(
    Math.sin(H),
    Math.cos(H) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi),
  );
  const azimuthDeg = (azFromSouth / RAD + 180 + 360) % 360;

  return { azimuthDeg, elevationDeg: altitude / RAD };
}

/** true se il sole è (praticamente) sotto l'orizzonte: tutto è in ombra. */
export function isNight(sun: SunPosition): boolean {
  return sun.elevationDeg <= 0.3;
}

/** Punto cardinale leggibile per l'azimut (es. "SO"). */
export function cardinalFromAzimuth(azimuthDeg: number): string {
  const names = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
  return names[Math.round((((azimuthDeg % 360) + 360) % 360) / 45) % 8];
}

// ---------------------------------------------------------------------------
// Alba, tramonto e casi polari — senza API: si campiona l'elevazione del sole
// nelle 24 h attorno all'istante richiesto e si cercano i passaggi all'orizzonte.
// ---------------------------------------------------------------------------

export type DaylightKind = 'normal' | 'polarDay' | 'polarNight';

export interface DaylightInfo {
  kind: DaylightKind;
  /** true se al tempo richiesto il sole è sopra l'orizzonte */
  isDay: boolean;
  /** primo tramonto dopo l'istante richiesto (ms epoch) */
  nextSunset?: number;
  /** prima alba dopo l'istante richiesto */
  nextSunrise?: number;
  /** ultima alba prima dell'istante richiesto */
  prevSunrise?: number;
  /** ultimo tramonto prima dell'istante richiesto */
  prevSunset?: number;
}

/**
 * `sunPosition` include già la rifrazione: per l'alba/tramonto "del lembo
 * superiore" resta da sottrarre il semidiametro apparente del sole (~0.27°).
 */
const HORIZON_ELEVATION_DEG = -0.27;

export function daylightInfo(tMs: number, lat: number, lng: number): DaylightInfo {
  const above = (ms: number) => sunPosition(ms, lat, lng).elevationDeg > HORIZON_ELEVATION_DEG;
  const STEP = 10 * 60_000;
  const from = tMs - 24 * 3_600_000;
  const to = tMs + 24 * 3_600_000;

  const events: Array<{ t: number; rise: boolean }> = [];
  let prev = above(from);
  for (let ms = from + STEP; ms <= to; ms += STEP) {
    const cur = above(ms);
    if (cur !== prev) {
      // bisezione fino a ~30 s
      let lo = ms - STEP;
      let hi = ms;
      for (let k = 0; k < 20; k++) {
        const mid = (lo + hi) / 2;
        if (above(mid) === prev) lo = mid;
        else hi = mid;
      }
      events.push({ t: hi, rise: cur });
    }
    prev = cur;
  }

  const isDay = above(tMs);
  if (events.length === 0) {
    return { kind: isDay ? 'polarDay' : 'polarNight', isDay };
  }
  const after = events.filter((e) => e.t > tMs);
  const before = events.filter((e) => e.t <= tMs).reverse();
  return {
    kind: 'normal',
    isDay,
    nextSunset: after.find((e) => !e.rise)?.t,
    nextSunrise: after.find((e) => e.rise)?.t,
    prevSunrise: before.find((e) => e.rise)?.t,
    prevSunset: before.find((e) => !e.rise)?.t,
  };
}
